import gql from 'graphql-tag';
import { clients } from '../../../util/clients';
import { fieldsToGqlString, mergeByIdNewAtTop } from '../../../util/shared/vue-global';
import { bulkResult } from '../../../util/shared/graphql-global';

const { backend } = clients.direct;

const apportionmnetFields = {
  _id: '',
  fromDate: '',
  toDate: '',
  section: '',
  fundNo: '',
  fundDesc: '',
  sourceNo: '',
  sourceDesc: '',
  unit: '',
  fund: '',
  amount: '',
  createdFrom: '',
  moneyFrom: '',
  remitUnit: '',
  isLocked: '',
  resaleAmount: '',
  comment: '',
  miscReceiptNo: '',
  miscDate: '',
  isSpecialApportionment: '',
  remitWarrantNumber: '',
  isNegativeAmount: '',
  isTrialRecord: '',
  // tag is stored as an enum/list type on the server; we don't need subfields
  // providing an empty object would generate `tag` with no selection which
  // triggers a GraphQL error. Use an empty string so only the scalar value is
  // returned.
  tag: '',
};

export const apportionmentTab = {
  namespaced: true,
  state: () => ({
    selectedItems: [],
  }),
  mutations: {
    setSelectedItems(state, items) {
      state.selectedItems = items;
    },
  },
  actions: {
    async loadApportionmentRecords({ commit, dispatch },
      { criteria, baseCrudKey }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql` query getApportionment($criteria: apportionmentCriteriaType) {
                      getApportionment(criteria: $criteria) {
                        ${fieldsToGqlString(apportionmnetFields)},
                        }
                      }`,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);

        if (results.data) {
          const apportionmentReportsData = results.data.getApportionment;
          commit('base/crud/setItems', [baseCrudKey, apportionmentReportsData], { root: true });
          return apportionmentReportsData || [];
        }
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem getting .', { root: true });
      }
      return [];
    },
    async apportionmentReportTotals({ commit, dispatch }, {
      criteria,
    }) {
      let results;
      try {
        const gqlfields = {
          totalAmount: '',
          negativeRecordsCount: '',
        };
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`
            query getApportionmentTotals($criteria: apportionmentCriteriaType) {
              getApportionmentTotals(criteria: $criteria) {
                ${fieldsToGqlString(gqlfields)},
              }
            }
          `,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        return results.data.getApportionmentTotals || [];
      } catch (e) {
        dispatch('logException', e, { root: true });
        clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem getting payment transactions list.', { root: true });
        return [];
      }
    },
    async upsertApportionmentTransaction({ dispatch, rootGetters, commit }, {
      item, fields, baseCrudKey,
    }) {
      let results;
      try {
        const gqlfields = fields || apportionmnetFields;
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation upsertApportionment ($item: apportionmentInputType!)  {
                upsertApportionment(upsert: $item) {
                    ${fieldsToGqlString(gqlfields)},
                  }
                }`,
          variables: {
            item,
          },
        });
        clients.handleGQLErrors(results, commit);
        const apportionmentTransStateData = rootGetters['base/crud/items'](baseCrudKey);
        const newTransaction = results.data ? (results.data.upsertApportionment
          || {}) : {};
        const newList = mergeByIdNewAtTop(apportionmentTransStateData || [], newTransaction);
        commit('base/crud/setItems', [baseCrudKey, newList], { root: true });
        return newTransaction;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem in upserting the transaction!', { root: true });
      }
      return {};
    },

    async upsertResaleTransaction({ dispatch, rootGetters, commit }, {
      item, fields, baseCrudKey,
    }) {
      let results;
      try {
        const gqlfields = fields || apportionmnetFields;
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation upsertResaleTransaction ($item: apportionmentInputType!)  {
                upsertResaleTransaction(upsert: $item) {
                    ${fieldsToGqlString(gqlfields)},
                  }
                }`,
          variables: {
            item,
          },
        });
        clients.handleGQLErrors(results, commit);
        const apportionmentTransStateData = rootGetters['base/crud/items'](baseCrudKey);
        if (results.data.upsertResaleTransaction) {
          const newTransaction = results.data ? (results.data.upsertResaleTransaction
            || {}) : {};
          const newList = mergeByIdNewAtTop(apportionmentTransStateData || [], newTransaction);
          commit('base/crud/setItems', [baseCrudKey, newList], { root: true });
          return newTransaction;
        }
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem in upserting the transaction!', { root: true });
      }
      return {};
    },
    async upsertForLockRecords({ commit, dispatch }, { criteria }) {
      let results;
      const updatedCriteria = { ...criteria };
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql` query apportionmentLockRecords($criteria: apportionmentCriteriaType) {
            apportionmentLockRecords(criteria: $criteria) {
                ${bulkResult},
              }
            }`,
          variables: {
            criteria: updatedCriteria,
          },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        const apportionmentLockUnlockResult = results.data ? results.data.apportionmentLockRecords
          || {} : {};
        return apportionmentLockUnlockResult;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was problem while locking/unlocking the transactions!', { root: true });
        return {};
      }
    },
    async apportionmnetUpdateRecordsInGL({ commit, dispatch }, { criteria }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql` query
              apportionmentUpdateGL ($criteria: apportionmentCriteriaType) {
                apportionmentUpdateGL(criteria: $criteria) {
                  upsertedCount
                  message,
                  status,
                  glUpdateToken,
                  glBalance {
                    transferIn,
                    transferOut,
                    difference,
                    unmatched { source, altKey, fundNo, amount },
                  },
                },
              }`,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        const result = results.data ? (results.data.apportionmentUpdateGL || {}) : {};
        if (result.status) {
          dispatch('flashSuccess', 'build records done Successfully in GL', { root: true });
        } else {
          return result;
        }
        return result;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There is a problem while creating records.', { root: true });
      }
      return null;
    },
    async deleteRecordFromApportionment({ commit, dispatch }, { criteria }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql` mutation apportionmentDeleteRecords($criteria: apportionmentDeleteCriteriaType) {
            apportionmentDeleteRecords(criteria: $criteria) {
              count,
              ok,
              message,
            }
          }`,
          variables: {
            criteria,
          },
        });
        clients.handleGQLErrors(results, commit);
        const apportionmentDeleteResult = results.data && results.data.apportionmentDeleteRecords ? results.data.apportionmentDeleteRecords : '';
        if (!apportionmentDeleteResult.ok) {
          dispatch('flashError', apportionmentDeleteResult.message || 'There was problem while Deleting the transactions!', { root: true });
          // return apportionmentDeleteResult;
        } else {
          dispatch('flashSuccess', apportionmentDeleteResult.message || 'Record(s) deleted successfully.', { root: true });
        }
        return apportionmentDeleteResult;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was problem while Deleting the transactions!', { root: true });
        return {};
      }
    },
    async assignWarrantNumbers({ dispatch, commit }, { criteria }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`
            mutation assignNextWarrantNumbers($criteria: apportionmentCriteriaType) {
              assignNextWarrantNumbers(criteria: $criteria) {
                remitWarrantNumberUpdates,
              }
            }
          `,
          variables: { criteria },
        });
        clients.handleGQLErrors(results, commit);
        const result = results.data
          ? (results.data.assignNextWarrantNumbers || {}).remitWarrantNumberUpdates : null;
        if (result === 'success') {
          dispatch('flashSuccess', 'Warrant numbers assigned successfully.', { root: true });
        } else {
          dispatch('flashError', 'Failed to assign warrant numbers.', { root: true });
        }
        return result;
      } catch (e) {
        dispatch('logException', e, { root: true });
        clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem assigning warrant numbers.', { root: true });
        throw e;
      }
    },
    async getEonOutOfBalanceFunds({ dispatch, commit }, { criteria }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`
            query eonOutOfBalanceFunds($criteria: eonAppropriationsCriteriaType) {
              eonOutOfBalanceFunds(criteria: $criteria) {
                fundNo,
                fundDescription,
                apportioned,
                approved,
                difference,
              }
            }
          `,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        return (results.data || {}).eonOutOfBalanceFunds || [];
      } catch (e) {
        dispatch('logException', e, { root: true });
        clients.handleGQLErrors(results, commit);
        // The check is advisory and must never block the print.
        dispatch('flashWarn', 'Could not check the appropriations balance; printing without the warning.', { root: true });
        return [];
      }
    },
    async createEonAppropriations({ dispatch, commit }, { criteria }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`
            mutation createEonAppropriations($criteria: eonAppropriationsCriteriaType) {
              createEonAppropriations(criteria: $criteria) {
                status,
                count,
                whomStr,
              }
            }
          `,
          variables: { criteria },
        });
        clients.handleGQLErrors(results, commit);
        const result = results.data ? (results.data.createEonAppropriations || {}) : {};
        if (result.status === 'OK') {
          dispatch('flashSuccess', `Created ${result.count} appropriation record(s) for ${result.whomStr}.`, { root: true });
        } else if (result.status === 'no funds to appropriate') {
          dispatch('flashWarn', 'No funds with activity were found to appropriate for this date range.', { root: true });
        } else if (result.status === 'missing required dates') {
          dispatch('flashWarn', 'Enter an Excise Board Date to create appropriations.', { root: true });
        } else {
          dispatch('flashError', 'There was a problem creating appropriation records.', { root: true });
        }
        return result;
      } catch (e) {
        dispatch('logException', e, { root: true });
        clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem creating appropriation records.', { root: true });
        throw e;
      }
    },
  },
};

export default apportionmentTab;
